import React, { useRef } from 'react'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import { media, phoneMaxWidth } from 'utils/media'
import { pluralize } from 'utils/pluralize'
import { LoadingCircle } from 'lib/LoadingCircle'
import { usePopoverController } from 'lib/Popover'
import { Text } from 'lib/Text'
import { TippyPopover } from 'lib/TippyPopover'
import { Clap } from 'modules/notifications/icons/Clap'
import { Review } from 'modules/notifications/icons/Review'
import { useUserFromPath } from 'modules/user/queries/user'
import { ReviewRatingAverage as OriginalReviewRatingAverage } from './ReviewRatingAverage'

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
`

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const InteractiveText = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
`

const ReviewIcon = styled(Review)`
  width: 24px;
  height: 24px;
`

const ClapIcon = styled(Clap)`
  width: 24px;
  height: 24px;
`

const RatingAverageWrapper = styled.div`
  width: 100%;
`
const ReviewRatingAverage = styled(OriginalReviewRatingAverage)`
  width: 100%;
  padding: 24px;

  ${media.phoneDown} {
    padding: 0;
  }
`

export const ReviewCounts = () => {
  const popoverControls = usePopoverController()
  const { data, loading } = useUserFromPath()
  const isMobile = useMedia(`(max-width: ${phoneMaxWidth}px)`)
  const childrenWrapperRef = useRef<HTMLDivElement | null>(null)

  if (!data?.user?.summary) {
    return null
  }

  const roundedAverageRating = data.user.summary.averageRating ? +data.user.summary.averageRating.toFixed(1) : 0

  const handleClickAverageItem = () => {
    if (!loading && isMobile) {
      popoverControls.setOpen(true)
    }
  }

  return (
    <Container>
      <Item>
        <ClapIcon />
        <Text as="p" size="ps">
          {!loading && pluralize(data.user.summary.totalUsefulReviews, 'clap received', 'claps received')}
        </Text>
      </Item>

      <TippyPopover
        content={
          <RatingAverageWrapper>
            <ReviewRatingAverage
              averageRating={roundedAverageRating}
              ratingDistributions={data.user.summary.ratingDistribution || []}
              totalReviews={data.user.summary.totalReviews || 0}
            />
          </RatingAverageWrapper>
        }
        defaultTriggerOnDesktop
        delay={[100, 300]}
        arrow
        controls={popoverControls}
        placement="top-start">
        <Item ref={childrenWrapperRef} role="button" aria-expanded={popoverControls.open} onClick={handleClickAverageItem}>
          <ReviewIcon />
          <InteractiveText as="p" size="ps">
            {loading ? <LoadingCircle /> : roundedAverageRating} average rating
          </InteractiveText>
        </Item>
      </TippyPopover>
    </Container>
  )
}
